import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { PageContainer } from "@/layouts/PageContainer";
import { Search } from "@/components/ui/Search";
import { Filter } from "@/components/ui/Filter";
import { DataTable } from "@/components/tables/DataTable";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useDebounce } from "@/hooks/useDebounce";
import { useScheduledRides } from "@/features/rides/useRides";
import { RideDetailDrawer } from "@/features/rides/RideDetailDrawer";
import { formatDateTime } from "@/utils/format";
import type { ScheduledRideRow } from "@/types/domain";

const ASSIGNMENT_OPTIONS = [
  { label: "Assigned", value: "assigned" },
  { label: "Unassigned", value: "unassigned" },
];

export default function ScheduledRides() {
  const [search, setSearch] = useState("");
  const [assignment, setAssignment] = useState("");
  const [page, setPage] = useState(1);
  const [selectedRideId, setSelectedRideId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search);

  const scheduledRides = useScheduledRides({
    page,
    limit: 20,
    search: debouncedSearch || undefined,
    assignment: (assignment || undefined) as "assigned" | "unassigned" | undefined,
  });

  const columns = useMemo<ColumnDef<ScheduledRideRow, unknown>[]>(
    () => [
      { header: "Ride", accessorKey: "code" },
      { header: "Rider", accessorKey: "rider" },
      { header: "Driver", accessorKey: "driver" },
      { header: "Scheduled at", accessorFn: (r) => formatDateTime(r.scheduledAt) },
      { header: "Pickup", accessorFn: (r) => r.pickup.name ?? r.pickup.area ?? "—" },
      { header: "Drop", accessorFn: (r) => r.drop.name ?? r.drop.area ?? "—" },
      {
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge status={row.original.status} label={row.original.statusLabel} alert={row.original.alert} />
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Scheduled Rides"
      description="Upcoming booked-ahead rides, dispatched but not yet started."
      actions={
        <>
          <Search value={search} onChange={setSearch} placeholder="Search ride code, rider…" />
          <Filter label="Driver" value={assignment} options={ASSIGNMENT_OPTIONS} onChange={setAssignment} />
        </>
      }
    >
      <div className="card">
        <DataTable
          columns={columns}
          data={scheduledRides.data?.items ?? []}
          isLoading={scheduledRides.isLoading}
          error={scheduledRides.error}
          onRetry={() => scheduledRides.refetch()}
          onRowClick={(r) => setSelectedRideId(r.id)}
          rowKey={(r) => r.id}
          emptyTitle="No scheduled rides"
          emptyDescription="Rides booked ahead of time will show up here once requested."
        />
        {scheduledRides.data && (
          <Pagination page={page} pageSize={20} total={scheduledRides.data.total} onPageChange={setPage} />
        )}
      </div>

      <RideDetailDrawer rideId={selectedRideId} onClose={() => setSelectedRideId(null)} />
    </PageContainer>
  );
}
